/**
 * Evidence fetcher for privately stored documents (dealer bank slips).
 *
 * The API never serves these statically — the bytes only come back through an
 * authenticated route — so a plain <img src> cannot load them. We fetch with
 * the bearer token and hand the caller an object URL to revoke.
 */
import { ADMIN_STORAGE_KEY, API_BASE_URL } from "./api";

function bearer(): string | null {
  try {
    const raw = localStorage.getItem(ADMIN_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Record<string, unknown>;
    return typeof stored?.access_token === "string" ? stored.access_token : null;
  } catch {
    return null;
  }
}

export async function fetchEvidenceUrl(path: string, signal?: AbortSignal): Promise<string> {
  const token = bearer();
  if (!token) throw new Error("Your session expired. Sign in again.");
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? "This document is no longer available."
        : `Could not open the document (${response.status}).`,
    );
  }
  const type = response.headers.get("content-type") ?? "application/octet-stream";
  const blob = await response.blob();
  return URL.createObjectURL(new Blob([blob], { type }));
}

export function releaseEvidenceUrl(url: string | null): void {
  if (url) URL.revokeObjectURL(url);
}
